// ConnectManager — the "+ → Connect external experience" flow (Loudlink Connect §5).
// One modal registers the experience (name / URL / type), the server creates the
// page_type='external' record AND auto-mints its named key, and the modal flips to a
// copy-it-once key reveal. revealKey() is shared with rotate (detail view) and the
// settings-cog key manager. Pop-token styled; no native dialogs.
var ConnectManager = (function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var CARD_CSS = 'background:var(--pop-bg, #1a1a1a);border:1px solid var(--pop-border, rgba(255,255,255,0.08));' +
    'border-radius:var(--pop-radius, 14px);box-shadow:var(--pop-shadow, 0 12px 40px rgba(0,0,0,0.4));' +
    'color:var(--pop-text, #f2f2f2);padding:24px;width:min(420px, calc(100vw - 32px));';
  var LABEL_CSS = 'display:block;font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;' +
    'color:var(--pop-text-muted, rgba(255,255,255,0.55));margin:0 0 6px;';
  var INPUT_CSS = 'width:100%;border:1px solid rgba(255,255,255,0.14);background:rgba(255,255,255,0.04);' +
    'color:var(--pop-text, #f2f2f2);border-radius:8px;padding:10px 12px;font-size:14px;margin-bottom:14px;';
  var BTN_CSS = 'border:none;border-radius:8px;padding:10px 18px;font-size:13px;font-weight:700;cursor:pointer;' +
    'background:var(--pop-text, #f2f2f2);color:var(--pop-bg, #1a1a1a);';
  var GHOST_CSS = 'border:1px solid rgba(255,255,255,0.14);border-radius:8px;padding:10px 18px;font-size:13px;' +
    'font-weight:600;cursor:pointer;background:transparent;color:var(--pop-text, #f2f2f2);';

  function overlayEl() {
    var overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:950;display:flex;align-items:center;justify-content:center;' +
      'background:var(--pop-scrim, rgba(0,0,0,0.45));';
    return overlay;
  }

  // Artist context: the switcher's selection, or the only artist in a single-artist workspace.
  function resolveArtists() {
    var artists = (window.ArtistSwitcher && ArtistSwitcher.getArtists && ArtistSwitcher.getArtists()) || [];
    var selected = (window.ArtistSwitcher && ArtistSwitcher.getSelectedArtistId && ArtistSwitcher.getSelectedArtistId()) || null;
    if (!selected) { try { selected = parseInt(localStorage.getItem('selectedArtistId'), 10) || null; } catch (_) {} }
    if (!selected && artists.length === 1) selected = artists[0].id;
    return { artists: artists, selected: selected };
  }

  function openCreateModal() {
    var ctx = resolveArtists();
    var overlay = overlayEl();
    var needsPicker = !ctx.selected && ctx.artists.length > 1;

    var card = document.createElement('div');
    card.style.cssText = CARD_CSS;
    card.innerHTML =
      '<h3 style="margin:0 0 6px;font-size:17px;font-weight:700;">Connect an experience</h3>' +
      '<p style="margin:0 0 18px;font-size:13px;color:var(--pop-text-muted, rgba(255,255,255,0.55));">' +
        'A Playable, AR filter or anything built outside the builder. It gets its own card, ' +
        'analytics, and an API key for signups + events.</p>' +
      (needsPicker
        ? '<span style="' + LABEL_CSS + '">Artist</span>' +
          '<select data-f="artist" style="' + INPUT_CSS + '">' +
            ctx.artists.filter(function (a) { return !a.is_org; }).map(function (a) {
              return '<option value="' + a.id + '">' + esc(a.name) + '</option>';
            }).join('') +
          '</select>'
        : '') +
      '<span style="' + LABEL_CSS + '">Name</span>' +
      '<input type="text" data-f="name" placeholder="e.g. Summer AR filter" maxlength="120" style="' + INPUT_CSS + '">' +
      '<span style="' + LABEL_CSS + '">Live URL <span style="text-transform:none;letter-spacing:0;font-weight:400;opacity:0.6;"> - where it\'s hosted</span></span>' +
      '<input type="url" data-f="url" placeholder="https://…" style="' + INPUT_CSS + '">' +
      '<span style="' + LABEL_CSS + '">Vanity link <span style="text-transform:none;letter-spacing:0;font-weight:400;opacity:0.6;"> - the pretty URL fans see (optional)</span></span>' +
      '<input type="text" data-f="slug" placeholder="e.g. game" maxlength="80" style="' + INPUT_CSS + 'margin-bottom:6px;">' +
      '<div data-slug-preview style="font-size:12px;color:var(--pop-text-muted, rgba(255,255,255,0.55));margin:0 0 14px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;word-break:break-all;"></div>' +
      '<span style="' + LABEL_CSS + '">Type</span>' +
      '<select data-f="type" style="' + INPUT_CSS + '">' +
        '<option value="playable">Playable</option>' +
        '<option value="ar">AR filter</option>' +
        '<option value="custom" selected>Custom</option>' +
      '</select>' +
      '<label style="display:flex;align-items:flex-start;gap:9px;font-size:12.5px;margin:0 0 14px;cursor:pointer;color:var(--pop-text, #f2f2f2);">' +
        '<input type="checkbox" data-f="pin" checked style="margin-top:2px;">' +
        '<span>Only accept signups &amp; analytics from this experience\'s own domain <span style="opacity:0.6;">(recommended - stops a leaked key being reused elsewhere)</span></span>' +
      '</label>' +
      '<div data-err style="min-height:18px;font-size:12.5px;color:#e2867c;margin:-4px 0 10px;"></div>' +
      '<div style="display:flex;gap:10px;justify-content:flex-end;">' +
        '<button type="button" data-act="cancel" style="' + GHOST_CSS + '">Cancel</button>' +
        '<button type="button" data-act="create" style="' + BTN_CSS + '">Connect</button>' +
      '</div>';
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    function close() { overlay.remove(); }
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    card.querySelector('[data-act="cancel"]').addEventListener('click', close);
    var nameInput = card.querySelector('[data-f="name"]');
    setTimeout(function () { nameInput.focus(); }, 0);

    // Client-side slugify mirroring the server (LoudLink.generateSlugFromString).
    function slugify(s) {
      return String(s || '').toLowerCase().trim()
        .replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
    }
    var domain = (window.PREVIEW_CONFIG && window.PREVIEW_CONFIG.productionDomain) || 'loud.link';
    var slugInput = card.querySelector('[data-f="slug"]');
    var slugPreview = card.querySelector('[data-slug-preview]');
    function currentArtist() {
      var sel = card.querySelector('[data-f="artist"]');
      var id = sel ? parseInt(sel.value, 10) : ctx.selected;
      return ctx.artists.filter(function (a) { return a.id === id; })[0] || null;
    }
    // Live "artistslug.loud.link/<slug>" preview; falls back to the name-derived slug when blank.
    function refreshPreview() {
      var a = currentArtist();
      var aslug = (a && a.slug) || 'artist';
      var s = slugify(slugInput.value) || slugify(nameInput.value) || 'your-experience';
      slugPreview.textContent = aslug + '.' + domain + '/' + s;
    }
    slugInput.addEventListener('input', refreshPreview);
    nameInput.addEventListener('input', refreshPreview);
    var artistSelForPreview = card.querySelector('[data-f="artist"]');
    if (artistSelForPreview) artistSelForPreview.addEventListener('change', refreshPreview);
    refreshPreview();

    card.querySelector('[data-act="create"]').addEventListener('click', function () {
      var btn = this;
      var errEl = card.querySelector('[data-err]');
      var artistSel = card.querySelector('[data-f="artist"]');
      var artistId = artistSel ? parseInt(artistSel.value, 10) : ctx.selected;
      var name = nameInput.value.trim();
      var url = card.querySelector('[data-f="url"]').value.trim();
      var type = card.querySelector('[data-f="type"]').value;
      var slug = slugInput.value.trim();
      var pin = card.querySelector('[data-f="pin"]').checked;
      errEl.textContent = '';
      if (!artistId) { errEl.textContent = 'Pick an artist first.'; return; }
      if (!name) { errEl.textContent = 'Give the experience a name.'; return; }
      if (!/^https?:\/\//i.test(url)) { errEl.textContent = 'Enter the full https:// URL it lives at.'; return; }

      btn.disabled = true; btn.textContent = 'Connecting…';
      fetch('/experiences/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artist_id: artistId, name: name, url: url, type: type, slug: slug, pin_origin: pin })
      })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          btn.disabled = false; btn.textContent = 'Connect';
          if (!d.success) { errEl.textContent = d.error || 'Failed - try again'; return; }
          close();
          window.dispatchEvent(new CustomEvent('connectExperienceCreated', { detail: d.experience }));
          revealKey(d.key.raw, {
            title: '“' + d.experience.name + '” is connected',
            subtitle: d.experience.share_url ? 'Fans reach it at ' + d.experience.share_url : null,
            doneLabel: 'Open its card',
            onDone: function () { window.location.href = '/experiences/' + d.experience.id; }
          });
        })
        .catch(function () { btn.disabled = false; btn.textContent = 'Connect'; errEl.textContent = 'Failed - try again'; });
    });
  }

  // The copy-it-once reveal — the ONLY time a raw key is ever on screen.
  function revealKey(rawKey, opts) {
    opts = opts || {};
    var overlay = overlayEl();
    var card = document.createElement('div');
    card.style.cssText = CARD_CSS;
    card.innerHTML =
      '<h3 style="margin:0 0 6px;font-size:17px;font-weight:700;">' + esc(opts.title || 'API key minted') + '</h3>' +
      (opts.subtitle ? '<p style="margin:0 0 12px;font-size:13px;font-weight:600;color:var(--pop-text, #f2f2f2);word-break:break-all;">' + esc(opts.subtitle) + '</p>' : '') +
      '<p style="margin:0 0 16px;font-size:13px;color:var(--pop-text-muted, rgba(255,255,255,0.55));">' +
        'This is the only time the key is shown - copy it into the experience now. ' +
        'You can rotate it later, but never see it again.</p>' +
      '<div style="display:flex;gap:8px;align-items:center;margin-bottom:18px;">' +
        '<code data-key style="flex:1;font-size:12.5px;padding:11px 12px;border-radius:8px;overflow-x:auto;white-space:nowrap;' +
          'background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12);font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">' +
          esc(rawKey) + '</code>' +
        '<button type="button" data-act="copy" style="' + GHOST_CSS + 'padding:11px 14px;flex-shrink:0;"><i class="bi bi-copy"></i></button>' +
      '</div>' +
      '<div style="display:flex;justify-content:flex-end;">' +
        '<button type="button" data-act="done" style="' + BTN_CSS + '">' + esc(opts.doneLabel || 'Done') + '</button>' +
      '</div>';
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    card.querySelector('[data-act="copy"]').addEventListener('click', function () {
      var self = this;
      navigator.clipboard && navigator.clipboard.writeText(rawKey).then(function () {
        self.innerHTML = '<i class="bi bi-check2"></i>';
        setTimeout(function () { self.innerHTML = '<i class="bi bi-copy"></i>'; }, 1600);
      });
    });
    // Deliberately NO backdrop-click close — losing an unseen key to a stray click is worse
    // than one extra button press.
    card.querySelector('[data-act="done"]').addEventListener('click', function () {
      overlay.remove();
      if (opts.onDone) opts.onDone();
    });
  }

  return { openCreateModal: openCreateModal, revealKey: revealKey };
})();
